import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import PasswordField from '../components/shell/PasswordField';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { GraduationCap, Buildings, ArrowRight } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { BRAND_NAME } from '../brand';
import { takeSignOutReason, signedOutMessage } from '../components/security/AwayGuard';

export default function LoginPage() {
  const [signedOutNote] = useState(() => signedOutMessage(takeSignOutReason()));
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, user, isAuthenticated } = useAuth();
  const navigate = useNavigate();

  // If user is already authenticated, redirect to appropriate dashboard
  useEffect(() => {
    if (isAuthenticated && user) {
      const routes = {
        super_admin: '/super-admin',
        tenant_admin: '/admin',
        instructor: '/instructor',
        parent: '/diary',
        learner: '/learn'
      };
      navigate(routes[user.role] || '/');
    }
  }, [isAuthenticated, user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(email, password, null);
      toast.success(`Welcome back, ${user.first_name}!`);
      
      // Redirect based on role
      const routes = {
        super_admin: '/super-admin',
        tenant_admin: '/admin',
        instructor: '/instructor',
        parent: '/diary',
        learner: '/learn'
      };
      navigate(routes[user.role] || '/');
    } catch (error) {
      const detail = error.response?.data?.detail;
      
      // Check if the error is about instructor/learner trying to login here
      if (error.response?.status === 403 && detail?.includes('organization portal')) {
        // Extract the portal URL from the error message
        const match = detail.match(/\/tenant\/([^/]+)\/login/);
        if (match) {
          toast.error(
            <div>
              <p className="font-medium">Wrong login page</p>
              <p className="text-sm mt-1">Please use your organization portal to login.</p>
            </div>,
            { duration: 5000 }
          );
          // Redirect to tenant login after a short delay
          setTimeout(() => {
            navigate(`/tenant/${match[1]}/login`);
          }, 2000);
        } else {
          toast.error('Instructors and Learners must login through their organization portal');
        }
      } else {
        const message = detail || 'Login failed. Please check your credentials.';
        toast.error(message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left side - Form */}
      <div className="flex items-center justify-center p-8 bg-background overflow-y-auto">
        <div className="w-full max-w-md space-y-6">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
                <GraduationCap className="w-6 h-6 text-primary-foreground" weight="duotone" />
              </div>
              <span className="font-heading font-bold text-xl">{BRAND_NAME}</span>
            </Link>
          </div>

          <Card className="border-0 shadow-none bg-transparent">
            <CardHeader className="px-0 pb-4">
              <CardTitle className="font-heading text-2xl">Platform Login</CardTitle>
              <CardDescription>
                Sign in as Super Admin or Tenant Admin.
                <span className="block mt-1 text-amber-600">
                  Instructors and Learners: Please use your organization portal.
                </span>
              </CardDescription>
            </CardHeader>
            <CardContent className="px-0 space-y-6">
              {signedOutNote && (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 text-[12px] text-amber-900"
                  data-testid="signed-out-reason">
                  {signedOutNote}
                </div>
              )}
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="field">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="admin@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    data-testid="email-input"
                  />
                </div>

                <PasswordField
  id="password"
  label="Password"
  value={password}
  onChange={(v) => setPassword(v)}
  placeholder="••••••••"
  required
  minLength={1}
  testid="password-input"
  generate
/>

                <Button type="submit" className="w-full" disabled={loading} data-testid="login-submit">
                  {loading ? 'Signing in...' : 'Sign In'}
                </Button>

                <div className="text-center">
                  <Link to="/forgot-password" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                    Forgot your password?
                  </Link>
                </div>
              </form>

              {/* Tenant Portal Links */}
              <div className="space-y-3 pt-4 border-t">
                <p className="text-sm font-medium text-muted-foreground">Organization Portals</p>
                
                {/* Register Organization */}
                <Link
                  to="/register-tenant"
                  className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted transition-colors group"
                  data-testid="register-tenant-link"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Buildings className="w-5 h-5 text-primary" weight="duotone" />
                    </div>
                    <div>
                      <p className="font-medium">Register New Organization</p>
                      <p className="text-sm text-muted-foreground">Create your own learning portal</p>
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 text-muted-foreground group-hover:text-foreground transition-colors" />
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Right side - Image */}
      <div className="hidden lg:block relative">
        <div className="absolute inset-0 bg-primary/5" />
        <img
          src="https://images.unsplash.com/photo-1758691736493-aa6d22c0f8a6?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzZ8MHwxfHNlYXJjaHwzfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwdHJhaW5pbmd8ZW58MHx8fHwxNzc0MzQ1MzAwfDA&ixlib=rb-4.1.0&q=85"
          alt="Corporate training"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12 text-white">
          <h2 className="font-heading text-3xl font-bold mb-4">Platform Administration</h2>
          <p className="text-lg opacity-90">
            Manage tenants, configure storage, and oversee the entire learning platform.
          </p>
        </div>
      </div>
    </div>
  );
}
